import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { calculateDistance, estimateDriveTime } from 'shared';
import { visitsApi, scheduleApi, storesApi, userApi } from '../services/api';
import { formatTimeCT, formatTimeCTInput, ctInputToUTC, formatDateLabel, localTZAbbr } from '../utils/timezone';
import type { VisitWithStore, StoreWithPriority } from 'shared';

interface Props {
  date: string;
  visits: VisitWithStore[];
  onClose: () => void;
}

interface EditEntry {
  visitId?: string;
  storeId: string;
  storeName: string;
  chainName: string | null;
  lat: number;
  lng: number;
  duration: number;
  startTimeCT: string;   // HH:MM 24h in Central time
  notes: string | null;
  visitType: 'regular' | 'training' | 'post_market';
  hotelRecommended: boolean;
}

interface RemoveDialog {
  entry: EditEntry;
  idx: number;
  phase: 'loading' | 'ready' | 'picking';
  suggestedDate: string | null;
  pickedDate: string;
}

function visitToEntry(v: VisitWithStore): EditEntry {
  return {
    visitId: v.id,
    storeId: v.store_id,
    storeName: v.store.name,
    chainName: v.store.chain_name,
    lat: v.store.lat,
    lng: v.store.lng,
    duration: v.duration_minutes,
    startTimeCT: formatTimeCTInput(v.start_datetime),
    notes: v.notes,
    visitType: v.visit_type,
    hotelRecommended: v.hotel_recommended,
  };
}

function addMinutes(hhmm: string, mins: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + mins;
  const nh = Math.min(Math.floor(total / 60), 23);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}

function endTime(start: string, dur: number): string {
  const t = addMinutes(start, dur);
  // Format as 12-hour
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

function driveMinBetween(aLat: number, aLng: number, bLat: number, bLng: number): number {
  return estimateDriveTime(calculateDistance(aLat, aLng, bLat, bLng));
}

function nextDay(date: string) {
  const d = new Date(date + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function buildGoogleMapsRouteUrl(
  home: { lat: number; lng: number } | null,
  stops: Array<{ lat: number; lng: number }>
): string {
  const base = 'https://www.google.com/maps/dir/?api=1';
  const origin = home ? `${home.lat},${home.lng}` : `${stops[0].lat},${stops[0].lng}`;
  const destination = home ? `${home.lat},${home.lng}` : `${stops[stops.length - 1].lat},${stops[stops.length - 1].lng}`;
  const waypoints = stops.map((s) => `${s.lat},${s.lng}`).join('|');
  return `${base}&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&waypoints=${encodeURIComponent(waypoints)}&travelmode=driving`;
}

const TYPE_STYLE: Record<string, { bg: string; border: string; dot: string; label: string }> = {
  training:    { bg: '#f3e8ff', border: '#c4b5fd', dot: '#7c3aed', label: 'Training' },
  post_market: { bg: '#ffedd5', border: '#fdba74', dot: '#ea580c', label: 'Post-Market' },
  regular:     { bg: '#dbeafe', border: '#93c5fd', dot: '#2563eb', label: 'Regular' },
};

export function DayDetailDrawer({ date, visits, onClose }: Props) {
  const queryClient = useQueryClient();

  const active = useMemo(
    () => [...visits].filter((v) => v.status !== 'cancelled').sort((a, b) => a.start_datetime.localeCompare(b.start_datetime)),
    [visits]
  );

  const [editMode, setEditMode] = useState(false);
  const [entries, setEntries] = useState<EditEntry[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [storeSearch, setStoreSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [rescheduled, setRescheduled] = useState<Array<{ name: string; date: string }>>([]);
  const [removeDialog, setRemoveDialog] = useState<RemoveDialog | null>(null);

  // Drag state
  const dragSrc = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [reordering, setReordering] = useState(false);

  const { data: allStores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
    enabled: editMode,
  });

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => userApi.get(),
  });

  const cancelVisitMutation = useMutation({
    mutationFn: (id: string) => visitsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedule'] }),
  });

  // ── Edit mode ─────────────────────────────────────────────────────────────

  function enterEdit() {
    setEntries(active.map(visitToEntry));
    setShowPicker(false);
    setStoreSearch('');
    setRescheduled([]);
    setRemoveDialog(null);
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
    setShowPicker(false);
    setRemoveDialog(null);
  }

  function updateEntry(idx: number, patch: Partial<EditEntry>) {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  }

  // ── Drag-and-drop ─────────────────────────────────────────────────────────

  function handleDragStart(e: React.DragEvent, idx: number) {
    dragSrc.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(idx);
  }

  async function handleDrop(e: React.DragEvent, idx: number) {
    e.preventDefault();
    const src = dragSrc.current;
    dragSrc.current = null;
    setDragOver(null);

    if (src === null || src === idx) return;

    // Reorder state immediately for visual feedback
    let reordered: EditEntry[] = [];
    setEntries((prev) => {
      const next = [...prev];
      const [moved] = next.splice(src, 1);
      next.splice(idx, 0, moved);
      reordered = next;
      return next;
    });

    // Auto-recalculate times using Google Maps drive times for existing visits
    const existingIds = reordered.filter((e) => e.visitId).map((e) => e.visitId!);
    if (existingIds.length > 1) {
      setReordering(true);
      try {
        const result = await scheduleApi.reorderDay(date, existingIds);
        // Update startTimeCT for each entry based on returned UTC datetimes
        setEntries((prev) => prev.map((entry) => {
          if (!entry.visitId) return entry;
          const updated = result.visits.find((v) => v.id === entry.visitId);
          if (!updated) return entry;
          return { ...entry, startTimeCT: formatTimeCTInput(updated.start) };
        }));
        queryClient.invalidateQueries({ queryKey: ['schedule'] });
      } catch {
        // silently keep manual order
      } finally {
        setReordering(false);
      }
    }
  }

  function handleDragEnd() {
    dragSrc.current = null;
    setDragOver(null);
  }

  // ── Remove / Reschedule ───────────────────────────────────────────────────

  async function initiateRemove(entry: EditEntry, idx: number) {
    if (!entry.visitId) {
      // Newly added this session — just drop it
      setEntries((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    setRemoveDialog({ entry, idx, phase: 'loading', suggestedDate: null, pickedDate: nextDay(date) });
    try {
      const result = await scheduleApi.slotStore(entry.storeId, nextDay(date));
      setRemoveDialog((d) => d ? { ...d, phase: 'ready', suggestedDate: result.scheduled_date } : null);
    } catch {
      setRemoveDialog((d) => d ? { ...d, phase: 'ready', suggestedDate: null } : null);
    }
  }

  async function confirmRemove(rescheduleDate: string | null) {
    if (!removeDialog) return;
    const { entry, idx } = removeDialog;
    setRemoveDialog(null);
    setEntries((prev) => prev.filter((_, i) => i !== idx));

    if (entry.visitId) {
      await cancelVisitMutation.mutateAsync(entry.visitId);
      if (rescheduleDate) {
        try {
          const result = await scheduleApi.slotStore(entry.storeId, rescheduleDate, entry.notes ?? undefined);
          setRescheduled((prev) => [...prev, { name: entry.storeName, date: result.scheduled_date }]);
        } catch {
          // reschedule failed silently
        }
      }
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
    }
  }

  // ── Add store ─────────────────────────────────────────────────────────────

  function addStore(store: StoreWithPriority) {
    const last = entries[entries.length - 1];
    const defaultTime = last ? addMinutes(last.startTimeCT, last.duration + 30) : '09:00';
    setEntries((prev) => [
      ...prev,
      {
        storeId: store.id,
        storeName: store.name,
        chainName: store.chain_name ?? null,
        lat: store.lat,
        lng: store.lng,
        duration: store.priority?.visit_duration_minutes ?? 60,
        startTimeCT: defaultTime,
        notes: null,
        visitType: 'regular',
        hotelRecommended: false,
      },
    ]);
    setShowPicker(false);
    setStoreSearch('');
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaving(true);
    try {
      await Promise.all(
        entries.map((entry) => {
          const startDt = ctInputToUTC(date, entry.startTimeCT);
          if (entry.visitId) {
            return visitsApi.update(entry.visitId, {
              start_datetime: startDt,
              duration_minutes: entry.duration,
            });
          }
          return visitsApi.create({
            store_id: entry.storeId,
            start_datetime: startDt,
            duration_minutes: entry.duration,
            visit_type: entry.visitType,
            notes: entry.notes ?? undefined,
          });
        })
      );
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const pickerStores = useMemo(() => {
    const onDay = new Set(entries.map((e) => e.storeId));
    return allStores
      .filter((s) => !onDay.has(s.id))
      .filter((s) => s.name.toLowerCase().includes(storeSearch.toLowerCase()));
  }, [allStores, entries, storeSearch]);

  const googleMapsUrl = useMemo(() => {
    const stops = editMode
      ? entries.map((e) => ({ lat: e.lat, lng: e.lng }))
      : active.map((v) => ({ lat: v.store.lat, lng: v.store.lng }));
    if (stops.length === 0) return null;
    return buildGoogleMapsRouteUrl(
      user?.home_lat && user?.home_lng ? { lat: user.home_lat, lng: user.home_lng } : null,
      stops
    );
  }, [active, entries, editMode, user]);

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 50 }}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: '480px', backgroundColor: '#fff', boxShadow: '-4px 0 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: 600, margin: 0 }}>
              {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h3>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '3px 0 0' }}>
              {active.length} {active.length === 1 ? 'visit' : 'visits'} · All times {localTZAbbr()}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {!editMode && googleMapsUrl && (
              <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '13px', padding: '6px 14px', borderRadius: '6px', background: '#2563eb', color: '#fff', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Open Route
              </a>
            )}
            {!editMode && (
              <button onClick={enterEdit}
                style={{ fontSize: '13px', padding: '5px 12px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', color: '#374151', fontWeight: 500 }}>
                Edit Route
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 2px' }}>×</button>
          </div>
        </div>

        {/* Notifications */}
        {rescheduled.length > 0 && (
          <div style={{ padding: '8px 16px', flexShrink: 0 }}>
            {rescheduled.map((r, i) => (
              <div key={i} style={{ padding: '8px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '13px', color: '#166534', marginBottom: '4px' }}>
                <strong>{r.name}</strong> rescheduled to {formatDateLabel(r.date)}
              </div>
            ))}
          </div>
        )}

        {editMode && !reordering && (
          <div style={{ margin: '8px 16px 0', padding: '8px 12px', backgroundColor: '#eff6ff', borderRadius: '6px', fontSize: '12px', color: '#1d4ed8', flexShrink: 0 }}>
            Drag ☰ handles to reorder — times auto-update using Google Maps drive times.
          </div>
        )}
        {reordering && (
          <div style={{ margin: '8px 16px 0', padding: '8px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '12px', color: '#166534', flexShrink: 0 }}>
            Recalculating route with Google Maps drive times…
          </div>
        )}

        {/* Remove / Reschedule dialog */}
        {removeDialog && (
          <div style={{ margin: '8px 16px 0', padding: '14px', backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '8px', flexShrink: 0 }}>
            <p style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 600, color: '#92400e' }}>
              Remove <em>{removeDialog.entry.storeName}</em> from today?
            </p>
            {removeDialog.phase === 'loading' && (
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Finding next available slot…</p>
            )}
            {removeDialog.phase === 'ready' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {removeDialog.suggestedDate && (
                  <button onClick={() => confirmRemove(removeDialog.suggestedDate)}
                    style={{ padding: '8px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, textAlign: 'left' }}>
                    Reschedule to {formatDateLabel(removeDialog.suggestedDate)} (suggested)
                  </button>
                )}
                <button onClick={() => setRemoveDialog((d) => d ? { ...d, phase: 'picking' } : null)}
                  style={{ padding: '8px 12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #93c5fd', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, textAlign: 'left' }}>
                  Pick a different date
                </button>
                <button onClick={() => confirmRemove(null)}
                  style={{ padding: '8px 12px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', textAlign: 'left' }}>
                  Just remove — I'll reschedule manually
                </button>
                <button onClick={() => setRemoveDialog(null)}
                  style={{ padding: '4px 8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#9ca3af' }}>
                  Cancel
                </button>
              </div>
            )}
            {removeDialog.phase === 'picking' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>Schedule from this date onward:</label>
                <input type="date" value={removeDialog.pickedDate}
                  min={nextDay(date)}
                  onChange={(e) => setRemoveDialog((d) => d ? { ...d, pickedDate: e.target.value } : null)}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => confirmRemove(removeDialog.pickedDate)}
                    style={{ flex: 1, padding: '8px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                    Reschedule
                  </button>
                  <button onClick={() => confirmRemove(null)}
                    style={{ padding: '8px 12px', background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                    Just remove
                  </button>
                  <button onClick={() => setRemoveDialog(null)}
                    style={{ padding: '8px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#9ca3af' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Visit list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {active.length === 0 && !editMode ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0', fontSize: '14px' }}>No visits scheduled</p>
          ) : editMode ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {entries.map((entry, idx) => {
                const s = TYPE_STYLE[entry.visitType] ?? TYPE_STYLE.regular;
                const isDraggingOver = dragOver === idx;
                return (
                  <div key={`${entry.storeId}-${idx}`}>
                    {/* Drive time gap before */}
                    {idx > 0 && (
                      <div style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '2px 0' }}>
                        ↓ ~{driveMinBetween(entries[idx - 1].lat, entries[idx - 1].lng, entry.lat, entry.lng)} min drive
                      </div>
                    )}
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDrop={(e) => handleDrop(e, idx)}
                      onDragEnd={handleDragEnd}
                      style={{
                        padding: '10px 12px',
                        backgroundColor: s.bg,
                        border: `1px solid ${isDraggingOver ? '#2563eb' : s.border}`,
                        borderRadius: '8px',
                        opacity: dragSrc.current === idx ? 0.5 : 1,
                        cursor: 'grab',
                      }}
                    >
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        {/* Drag handle */}
                        <div style={{ fontSize: '16px', color: '#9ca3af', cursor: 'grab', paddingTop: '2px', flexShrink: 0 }} title="Drag to reorder">☰</div>

                        {/* Stop number */}
                        <span style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: s.dot, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0, marginTop: '1px' }}>
                          {idx + 1}
                        </span>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{entry.storeName}</div>
                          {entry.chainName && <div style={{ fontSize: '11px', color: '#6b7280' }}>{entry.chainName}</div>}
                          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Start ({localTZAbbr()})</label>
                              <input type="time" value={entry.startTimeCT}
                                onChange={(e) => updateEntry(idx, { startTimeCT: e.target.value })}
                                style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', width: '100px' }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '2px' }}>Duration</label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <input type="number" value={entry.duration} min={15} step={15}
                                  onChange={(e) => updateEntry(idx, { duration: parseInt(e.target.value) || 15 })}
                                  style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px', width: '60px' }}
                                />
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>min</span>
                              </div>
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280', alignSelf: 'flex-end', paddingBottom: '6px' }}>
                              ends {endTime(entry.startTimeCT, entry.duration)}
                            </div>
                          </div>
                        </div>

                        {/* Remove */}
                        <button onClick={() => initiateRemove(entry, idx)}
                          style={{ fontSize: '12px', color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', flexShrink: 0, fontWeight: 500, marginTop: '1px' }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Store picker */}
              {showPicker && (
                <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#fff', marginTop: '4px' }}>
                  <div style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb' }}>
                    <input autoFocus type="text" placeholder="Search stores…"
                      value={storeSearch}
                      onChange={(e) => setStoreSearch(e.target.value)}
                      style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {pickerStores.length === 0 ? (
                      <p style={{ padding: '12px', color: '#9ca3af', fontSize: '13px', textAlign: 'center' }}>
                        {storeSearch ? 'No matches' : 'All stores already on this day'}
                      </p>
                    ) : pickerStores.map((store) => (
                      <button key={store.id} onClick={() => addStore(store)}
                        style={{ display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '13px', color: '#111827' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                      >
                        <span style={{ fontWeight: 500 }}>{store.name}</span>
                        {store.chain_name && <span style={{ color: '#9ca3af', marginLeft: '6px', fontSize: '12px' }}>{store.chain_name}</span>}
                      </button>
                    ))}
                  </div>
                  <div style={{ padding: '8px 10px', borderTop: '1px solid #e5e7eb' }}>
                    <button onClick={() => { setShowPicker(false); setStoreSearch(''); }}
                      style={{ fontSize: '13px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Read mode */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {active.map((visit, idx) => {
                const s = TYPE_STYLE[visit.visit_type] ?? TYPE_STYLE.regular;
                return (
                  <div key={visit.id}>
                    {idx > 0 && (
                      <div style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af', padding: '2px 0' }}>
                        ↓ ~{driveMinBetween(active[idx - 1].store.lat, active[idx - 1].store.lng, visit.store.lat, visit.store.lng)} min drive
                      </div>
                    )}
                    <div style={{ padding: '10px 12px', backgroundColor: s.bg, border: `1px solid ${s.border}`, borderRadius: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                        <span style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: s.dot, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0, marginTop: '1px' }}>
                          {idx + 1}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{visit.store.name}</div>
                          {visit.store.chain_name && <div style={{ fontSize: '11px', color: '#6b7280' }}>{visit.store.chain_name}</div>}
                          <div style={{ fontSize: '12px', color: '#374151', marginTop: '4px' }}>
                            {formatTimeCT(visit.start_datetime)} – {formatTimeCT(visit.end_datetime)}
                            <span style={{ margin: '0 5px', color: '#9ca3af' }}>·</span>
                            {visit.duration_minutes} min
                            {visit.hotel_recommended && <span style={{ marginLeft: '6px' }} title="Hotel recommended tonight">🏨</span>}
                          </div>
                          {visit.notes && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px', fontStyle: 'italic' }}>{visit.notes}</div>}
                        </div>
                        <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: '4px', backgroundColor: s.dot + '22', color: s.dot, fontWeight: 500, flexShrink: 0 }}>
                          {s.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          {editMode ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { setShowPicker(true); setStoreSearch(''); }} disabled={showPicker}
                style={{ padding: '10px 14px', border: '1px solid #2563eb', borderRadius: '6px', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: 500, flexShrink: 0 }}>
                + Add Store
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: '10px', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelEdit}
                style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={enterEdit}
              style={{ width: '100%', padding: '11px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 500, cursor: 'pointer', fontSize: '14px' }}>
              Edit Route
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
